/**
 * Shared grade calculation utilities.
 * These thresholds align with the IB grade scale defined in the database.
 *
 * Grade Scale (IB-aligned):
 * - A+ (7): 95-100%
 * - A  (6): 87-94%
 * - A- (6): 82-86%
 * - B+ (5): 77-81%
 * - B  (5): 72-76%
 * - B- (5): 67-71%
 * - C+ (4): 62-66%
 * - C  (4): 57-61%
 * - C- (4): 52-56%
 * - D+ (3): 47-51%
 * - D  (3): 42-46%
 * - D- (2): 37-41%
 * - F  (1): 0-36%
 */

export type LetterGrade =
  | 'A+'
  | 'A'
  | 'A-'
  | 'B+'
  | 'B'
  | 'B-'
  | 'C+'
  | 'C'
  | 'C-'
  | 'D+'
  | 'D'
  | 'D-'
  | 'F';

export type IbGrade = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export interface GradeThreshold {
  minPercentage: number;
  maxPercentage: number;
  letterGrade: LetterGrade;
  ibGrade: IbGrade;
  gpaPoints: number;
}

/**
 * Grade scale thresholds aligned with IB standards.
 * Sorted from highest to lowest for lookup efficiency.
 */
export const GRADE_THRESHOLDS: readonly GradeThreshold[] = [
  { minPercentage: 95, maxPercentage: 100, letterGrade: 'A+', ibGrade: 7, gpaPoints: 5.0 },
  { minPercentage: 87, maxPercentage: 94, letterGrade: 'A', ibGrade: 6, gpaPoints: 4.7 },
  { minPercentage: 82, maxPercentage: 86, letterGrade: 'A-', ibGrade: 6, gpaPoints: 4.5 },
  { minPercentage: 77, maxPercentage: 81, letterGrade: 'B+', ibGrade: 5, gpaPoints: 4.3 },
  { minPercentage: 72, maxPercentage: 76, letterGrade: 'B', ibGrade: 5, gpaPoints: 4.0 },
  { minPercentage: 67, maxPercentage: 71, letterGrade: 'B-', ibGrade: 5, gpaPoints: 3.7 },
  { minPercentage: 62, maxPercentage: 66, letterGrade: 'C+', ibGrade: 4, gpaPoints: 3.3 },
  { minPercentage: 57, maxPercentage: 61, letterGrade: 'C', ibGrade: 4, gpaPoints: 3.0 },
  { minPercentage: 52, maxPercentage: 56, letterGrade: 'C-', ibGrade: 4, gpaPoints: 2.7 },
  { minPercentage: 47, maxPercentage: 51, letterGrade: 'D+', ibGrade: 3, gpaPoints: 2.3 },
  { minPercentage: 42, maxPercentage: 46, letterGrade: 'D', ibGrade: 3, gpaPoints: 2.0 },
  { minPercentage: 37, maxPercentage: 41, letterGrade: 'D-', ibGrade: 2, gpaPoints: 1.7 },
  { minPercentage: 0, maxPercentage: 36, letterGrade: 'F', ibGrade: 1, gpaPoints: 1.0 },
] as const;

/**
 * Does a percentage fall in this band?
 *
 * The bands above are inclusive *integer* ranges, so a fractional percentage such
 * as 94.2 sits between two of them and satisfies neither `>= 95` nor `<= 94`. The
 * school's gradebook workbook resolves this with strict `>` comparisons against
 * the band below - `IF(x>94,7, IF(x>81,6, ...))` - which puts 94.2 in the top band.
 *
 * Because the bands are contiguous, the band below always ends at
 * `minPercentage - 1`, so `percentage > minPercentage - 1` reproduces the workbook
 * exactly while agreeing with `>= minPercentage` for every whole number.
 */
function matchesThreshold(percentage: number, threshold: GradeThreshold): boolean {
  return percentage > threshold.minPercentage - 1;
}

/**
 * Calculate IB grade (1-7) from percentage.
 * Used as fallback when no grade scales are configured in the database.
 */
export function calculateIbGrade(percentage: number): IbGrade {
  const threshold = GRADE_THRESHOLDS.find((t) => matchesThreshold(percentage, t));
  return threshold?.ibGrade ?? 1;
}

/**
 * Calculate letter grade from percentage.
 * Used as fallback when no grade scales are configured in the database.
 */
export function calculateLetterGrade(percentage: number): LetterGrade {
  const threshold = GRADE_THRESHOLDS.find((t) => matchesThreshold(percentage, t));
  return threshold?.letterGrade ?? 'F';
}

/**
 * Calculate GPA points from percentage.
 */
export function calculateGpaPoints(percentage: number): number {
  const threshold = GRADE_THRESHOLDS.find((t) => matchesThreshold(percentage, t));
  return threshold?.gpaPoints ?? 1.0;
}

/**
 * Get full grade info from percentage.
 */
export function getGradeInfo(percentage: number): GradeThreshold {
  const threshold = GRADE_THRESHOLDS.find((t) => matchesThreshold(percentage, t));
  return (
    threshold ?? {
      minPercentage: 0,
      maxPercentage: 36,
      letterGrade: 'F' as const,
      ibGrade: 1 as const,
      gpaPoints: 1.0,
    }
  );
}

/*
 * Grade tint ramp.
 *
 * These follow the same five-step color language as the design system's
 * `.grade-a`…`.grade-f` / `.grade-1`…`.grade-7` badge classes in
 * @gradebook/ui's styles.css: success -> secondary -> info -> warning -> danger,
 * with `neutral` for "no grade". They previously used stock Tailwind scales on a
 * different ramp (green -> blue -> amber -> orange -> red), which meant a row
 * tint and the grade badge sitting in it could disagree about what colour a 5
 * is. `orange` in particular has no equivalent in the design system.
 */
export function getGradeColorClass(percentage: number | undefined | null): string {
  if (percentage == null) return 'bg-neutral-50';
  if (percentage >= 90) return 'bg-success-50';
  if (percentage >= 80) return 'bg-secondary-50';
  if (percentage >= 70) return 'bg-info-50';
  if (percentage >= 60) return 'bg-warning-50';
  return 'bg-danger-50';
}

/**
 * Hover counterpart to {@link getGradeColorClass} — same ramp, one step darker.
 */
export function getGradeHoverColorClass(percentage: number | undefined | null): string {
  if (percentage == null) return 'hover:bg-neutral-100';
  if (percentage >= 90) return 'hover:bg-success-100';
  if (percentage >= 80) return 'hover:bg-secondary-100';
  if (percentage >= 70) return 'hover:bg-info-100';
  if (percentage >= 60) return 'hover:bg-warning-100';
  return 'hover:bg-danger-100';
}
